/**
 * Sticky Header Implementation
 *
 * Behavior:
 * - On initial page load at scroll position 0: No sticky header
 * - Scrolling DOWN past header height: Header hides instantly
 * - Scrolling UP (after scrolling past header height): Header becomes sticky
 *
 * Supports two modes:
 * - Homepage (transparent-header class): Header overlaps content, no placeholder needed
 * - Other pages: Header in normal flow, placeholder prevents layout jump
 *
 * Accounts for:
 * - Admin bar (32px desktop, 46px mobile)
 * - Countdown bar (can be closed by user)
 */

(function () {
 'use strict';

 const scrollbarWidth2 = window.innerWidth - document.documentElement.clientWidth;
 document.documentElement.setAttribute('style', `--scrollbarW: ${scrollbarWidth2}px`);

 const CONFIG = {
  headerSelector: 'header.wp-block-template-part',
  countdownSelector: '.countdown-timer-block',
  adminBarSelector: '#wpadminbar',
  scrollThreshold: 5,
 };

 let header = null;
 let placeholder = null;
 let lastScrollY = 0;
 let scrollDirection = 'none';
 let ticking = false;
 let isTransparentHeader = false;
 let hasScrolledPastHeader = false; // Track if user has ever scrolled past header height

 /**
  * Calculate total header height and set as CSS variable on the header element
  * This accounts for countdown timer visibility
  */
 function updateHeaderHeightVariable() {
  if (!header) return;

  const headerHeight = header.offsetHeight;
  header.style.setProperty('--header-height', `${headerHeight}px`);
 }

 /**
  * Get admin bar height (32px desktop, 46px mobile when logged in)
  */
 function getAdminBarHeight() {
  const adminBar = document.querySelector(CONFIG.adminBarSelector);
  if (!adminBar) return 0;
  return adminBar.offsetHeight || 0;
 }

 /**
  * Check if countdown is visible (not closed by user)
  */
 function isCountdownVisible() {
  const countdown = document.querySelector(CONFIG.countdownSelector);
  if (!countdown) return false;

  // Check if countdown is hidden via display:none (set when user closes it)
  const style = getComputedStyle(countdown);
  if (style.display === 'none') return false;

  // Check offsetParent for visibility
  if (countdown.offsetParent === null && style.position !== 'fixed') return false;

  return true;
 }

 /**
  * Get countdown height (only if visible)
  */
 function getCountdownHeight() {
  if (!isCountdownVisible()) return 0;
  const countdown = document.querySelector(CONFIG.countdownSelector);
  return countdown ? countdown.offsetHeight : 0;
 }

 /**
  * Calculate the effective header height for sticky trigger
  * This is the actual visible header height that user needs to scroll past
  */
 function getEffectiveHeaderHeight() {
  if (!header) return 0;

  // Get the full header height
  let headerHeight = header.offsetHeight;

  // If countdown is hidden (closed by user), we need to account for this
  // The header.offsetHeight already reflects current state, so no adjustment needed

  return headerHeight;
 }

 function createPlaceholder() {
  if (placeholder) return;

  placeholder = document.createElement('div');
  placeholder.className = 'wp-header-sticky-placeholder';

  if (header && header.parentNode) {
   header.parentNode.insertBefore(placeholder, header.nextSibling);
  }
 }

 function updatePlaceholderHeight() {
  if (!header || !placeholder) return;

  // Use the effective header height for placeholder
  const height = getEffectiveHeaderHeight();
  placeholder.style.height = `${height}px`;
 }

 function showPlaceholder() {
  if (isTransparentHeader) return;
  if (placeholder) {
   placeholder.style.display = 'block';
   updatePlaceholderHeight();
  }
 }

 function hidePlaceholder() {
  if (placeholder) {
   placeholder.style.display = 'none';
  }
 }

 function getScrollDirection(currentScrollY) {
  const diff = currentScrollY - lastScrollY;
  if (Math.abs(diff) < CONFIG.scrollThreshold) {
   return scrollDirection;
  }
  return diff > 0 ? 'down' : 'up';
 }

 function applyHeaderState(state) {
  if (!header) return;

  header.classList.remove(
   'wp-header--hidden',
   'wp-header--visible',
   'wp-header--fixed',
   'wp-header--top'
  );

  switch (state) {
   case 'top':
    header.classList.add('wp-header--top');
    hidePlaceholder();
    break;
   case 'hidden':
    header.classList.add('wp-header--hidden', 'wp-header--fixed');
    setTimeout(() => {
     header.classList.add('wp-header-transition');
    }, 200);
    showPlaceholder();
    break;
   case 'visible':
    header.classList.add('wp-header--visible', 'wp-header--fixed');
    showPlaceholder();
    break;
  }
 }

 function handleScroll() {
  const currentScrollY = window.scrollY;
  const newDirection = getScrollDirection(currentScrollY);
  const effectiveHeaderHeight = getEffectiveHeaderHeight();
  const isAtTop = currentScrollY <= 0;
  const isPastHeaderHeight = currentScrollY > effectiveHeaderHeight;

  // Track if user has ever scrolled past header height in this session
  if (isPastHeaderHeight) {
   hasScrolledPastHeader = true;
  }

  if (isAtTop) {
   // At the very top - reset to top state and reset the scroll tracking
   applyHeaderState('top');
   header.classList.remove('wp-header-transition');
   hasScrolledPastHeader = false;
  } else if (currentScrollY <= effectiveHeaderHeight) {
   // Still within header height zone
   // Only show sticky if user has previously scrolled past header and is now scrolling up
   if (hasScrolledPastHeader && newDirection === 'up') {
    applyHeaderState('visible');
   } else {
    // User hasn't scrolled past header yet, or is scrolling down within header zone
    applyHeaderState('top');
    header.classList.remove('wp-header-transition');
   }
  } else if (newDirection === 'down' && isPastHeaderHeight) {
   // Scrolling down past header height - hide the header
   applyHeaderState('hidden');
  } else if (newDirection === 'up' && isPastHeaderHeight) {
   // Scrolling up while past header height - show sticky header
   applyHeaderState('visible');
  }

  scrollDirection = newDirection;
  lastScrollY = currentScrollY;
  ticking = false;
 }

 function onScroll() {
  if (!ticking) {
   window.requestAnimationFrame(handleScroll);
   ticking = true;
  }
 }

 /**
  * Set up observer for countdown close button clicks
  */
 function observeCountdownClose() {
  const countdown = document.querySelector(CONFIG.countdownSelector);
  if (!countdown) return;

  // Watch for style changes (display: none when closed)
  const observer = new MutationObserver((mutations) => {
   mutations.forEach((mutation) => {
    if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
     // Countdown visibility changed, update placeholder and recalculate
     updatePlaceholderHeight();
     // Update header height CSS variable
     updateHeaderHeightVariable();
     // Re-trigger scroll handling to update header state
     if (!ticking) {
      window.requestAnimationFrame(handleScroll);
      ticking = true;
     }
    }
   });
  });

  observer.observe(countdown, { attributes: true, attributeFilter: ['style'] });
 }

 function init() {
  header = document.querySelector(CONFIG.headerSelector);
  if (!header) return;

  isTransparentHeader = header.classList.contains('transparent-header');

  if (!isTransparentHeader) {
   createPlaceholder();
  }

  updatePlaceholderHeight();
  updateHeaderHeightVariable();
  lastScrollY = window.scrollY;

  // Set initial state - always start with 'top' state
  // Only apply fixed/visible state if we're past header height AND this is a page reload at scroll position
  header.classList.add('wp-header--top');

  const effectiveHeaderHeight = getEffectiveHeaderHeight();
  if (lastScrollY <= 0) {
   // At top of page - no sticky
   applyHeaderState('top');
  } else if (lastScrollY > effectiveHeaderHeight) {
   // Page was reloaded while scrolled past header - show sticky visible
   hasScrolledPastHeader = true;
   applyHeaderState('visible');
  } else {
   // Page was reloaded while within header zone - keep top state
   applyHeaderState('top');
  }

  // Set up observers and listeners
  observeCountdownClose();
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', () => {
   updatePlaceholderHeight();
   updateHeaderHeightVariable();
   // Recalculate on resize
   if (!ticking) {
    window.requestAnimationFrame(handleScroll);
    ticking = true;
   }
  }, { passive: true });
 }

 if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
 } else {
  init();
 }
})(); 